import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

type CampoTecnicoProps = {
  label: string;
  value: string | number | null | undefined;
  isIp?: boolean;
  isStatus?: boolean;
};

function CampoTecnico({ label, value, isIp, isStatus }: CampoTecnicoProps) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      {isIp && value && value !== '-' ? (
        <Typography
          variant="body1"
          fontWeight={500}
          component="a"
          href={`http://${value}:80`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {value}
        </Typography>
      ) : isStatus ? (
        <Chip
          label={value ?? '-'}
          size="small"
          color={value === 'ACTIVO' ? 'success' : value === 'SUSPENDIDO' ? 'warning' : 'error'}
          sx={{ fontWeight: 600 }}
        />
      ) : (
        <Typography variant="body1" fontWeight={500}>
          {value ?? '-'}
        </Typography>
      )}
    </Grid>
  );
}

type TabDatosTecnicosProps = {
  clienteData: any;
};

export default function TabDatosTecnicos({ clienteData }: TabDatosTecnicosProps) {
  const contratos = clienteData?.contratos || [];

  if (contratos.length === 0) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
          No hay datos tecnicos disponibles.
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      {contratos.map((contrato: any, index: number) => {
        // datos_tecnicos viene como objeto anidado desde el sync Softnet.
        // Si no esta presente usamos un objeto vacio para no romper accesos.
        const dt = contrato.datos_tecnicos || {};
        const equipos: any[] = Array.isArray(contrato.equipos) ? contrato.equipos : [];
        const materiales: any[] = Array.isArray(contrato.materiales) ? contrato.materiales : [];

        return (
          <Card variant="outlined" key={contrato.id_contrato || index} sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {contratos.length > 1
                  ? `Datos Tecnicos - Contrato ${contrato.codigo || index + 1}`
                  : 'Datos Tecnicos'}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <CampoTecnico label="Estado Servicio" value={contrato.estado_servicio || contrato.estado_contrato} isStatus />
                <CampoTecnico label="Plan" value={contrato.plan} />
                <CampoTecnico label="Velocidad" value={contrato.velocidad} />
                <CampoTecnico label="Estado Navegacion" value={contrato.estado_navegacion} />

                <CampoTecnico label="OLT" value={dt.olt || contrato.olt_nombre} />
                <CampoTecnico label="OLT IP" value={contrato.olt_ip} isIp />
                <CampoTecnico label="NAP Nombre" value={contrato.nap_nombre} />
                <CampoTecnico label="Puerto" value={contrato.puerto} />

                <CampoTecnico label="Serie ONT" value={contrato.serie_ont} />
                <CampoTecnico label="IP (IPv4)" value={dt.ipv4 || contrato.ipv4_actual} isIp />
                <CampoTecnico label="IPv4 Pública" value={dt.ipv4_publica} isIp />
                <CampoTecnico label="Radio Base" value={dt.radio_base} />

                <CampoTecnico label="PPPoE" value={contrato.pppoe} />
                <CampoTecnico label="Usuario PPPoE" value={dt.usuario_pppoe} />
                <CampoTecnico label="Clave PPPoE" value={dt.clave_pppoe} />

                <CampoTecnico label="Potencia (dBm)" value={dt.potencia_dbm} />
                <CampoTecnico label="Distancia (m)" value={dt.distancia} />
                <CampoTecnico label="Huawei Domain previo" value={dt.previous_huawei_domain || '-'} />
                <CampoTecnico label="Técnico Instalador" value={contrato.tecnico_instalador || '-'} />
              </Grid>

              {/* Equipos */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Equipos ({equipos.length})
                </Typography>
                {equipos.length === 0 ? (
                  <Alert severity="info" variant="outlined">Sin equipos asignados.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Marca / Modelo</TableCell>
                          <TableCell>Serie MAC</TableCell>
                          <TableCell>Serie Num</TableCell>
                          <TableCell align="right">Cantidad</TableCell>
                          <TableCell>Observaciones</TableCell>
                          <TableCell>Fecha</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {equipos.map((e: any, eidx: number) => (
                          <TableRow key={e.id_detalle_contrato ?? eidx} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{e.modelo || '-'}</Typography>
                              {e.marca && <Typography variant="caption" color="text.secondary">{e.marca}</Typography>}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{e.serie_mac || '-'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{e.serie_num || '-'}</TableCell>
                            <TableCell align="right">{e.cantidad ?? '-'}</TableCell>
                            <TableCell>{e.observaciones || '-'}</TableCell>
                            <TableCell>{e.fecha_sistema || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              {/* Materiales */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Materiales ({materiales.length})
                </Typography>
                {materiales.length === 0 ? (
                  <Alert severity="info" variant="outlined">Sin materiales registrados.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Material</TableCell>
                          <TableCell>Código</TableCell>
                          <TableCell align="right">Cantidad</TableCell>
                          <TableCell align="right">Costo</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {materiales.map((m: any, midx: number) => (
                          <TableRow key={m.id ?? midx} hover>
                            <TableCell>
                              <Chip
                                label={(m.tipo || '-').toUpperCase()}
                                size="small"
                                variant="outlined"
                                color={m.tipo === 'instalacion' ? 'primary' : 'default'}
                              />
                            </TableCell>
                            <TableCell>{m.tie_nombre || '-'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{m.codigo || '-'}</TableCell>
                            <TableCell align="right">{m.cantidad ?? '-'}</TableCell>
                            <TableCell align="right">{m.costo ? `$${m.costo}` : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Grid>
  );
}
